import React from 'react';
import { DashboardContainer } from '../../components/ui/UtilsLayout';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Eye, Zap, Clock, Layers, TrendingDown, Folder, Calendar, Settings, Download } from 'lucide-react';
import { OverallAnalytics } from '../../utils/imagePath';

// Custom label renderer for donut chart
const renderCustomLabel = () => {
  return null; // Return null since we're using absolute positioned div labels
};

const Analytics = () => {
  // Colors
  const COLORS = {
    primary: '#5959DC',   // Blue/Purple
    secondary: '#D9232A', // Red
    accent: '#47D176',    // Green
  };

  // Engagement Trend Data
  const engagementData = [
    { day: 'Tue', engagement: 45 },
    { day: 'Wed', engagement: 50 },
    { day: 'Thu', engagement: 55 },
    { day: 'Fri', engagement: 62 },
    { day: 'Sat', engagement: 65 },
    { day: 'Sun', engagement: 68 },
  ];

  // Device Distribution Data
const deviceData = [
  { name: 'MR', value: 19, color: '#D9232A' },   // Red - top left
  { name: 'WEB', value: 19, color: '#47D176' },  // Green - right
  { name: 'VR', value: 62, color: '#5959DC' }    // Blue - bottom left (largest)
];

  // Top Performing Scenes
  const topScenes = [
    { id: 1, title: 'Product Overview', views: 2400, color: COLORS.primary },
    { id: 2, title: '"Start Free Trial" Button', clicks: 847, color: COLORS.accent },
  ];

  return (
    <DashboardContainer>
      <div className="w-full h-full p-6 md:p-8">
        {/* Header Section with Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div className="mb-6 md:mb-0">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Analytics</h2>
            <p className="text-gray-400 text-lg">Comprehensive insights across the SpatialGrid platform</p>
          </div>

          {/* Top Right Controls */}
          <div className="flex flex-wrap gap-3 items-center justify-end">
            <button className="flex items-center gap-2 bg-[#31333A] border border-gray-700 text-gray-300 px-4 py-2 rounded-lg hover:bg-[#3A3C42] transition">
              <Calendar size={18} />
              <span className="text-sm">March 18 - April 18</span>
            </button>

            <select className="flex items-center gap-2 bg-[#31333A] border border-gray-700 text-gray-300 px-4 py-2 rounded-lg hover:bg-[#3A3C42] transition cursor-pointer">
              <option>Select Experience</option>
            </select>

            <button className="bg-[#31333A] border border-gray-700 text-gray-300 p-2 rounded-lg hover:bg-[#3A3C42] transition">
              <Settings size={18} />
            </button>

            <button className="bg-[#31333A] border border-gray-700 text-gray-300 p-2 rounded-lg hover:bg-[#3A3C42] transition">
              <Download size={18} />
            </button>
          </div>
        </div>

        {/* Top Metrics Cards */}
        <div className="bg-[#1E1F22] rounded-[10px] p-6 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <MetricCard
              icon={<Eye size={24} />}
              label="Total views"
              value="21.7K"
            />
            <MetricCard
              icon={<Zap size={24} />}
              label="Total Clicks"
              value="19.7K"
            />
            <MetricCard
              icon={<Clock size={24} />}
              label="Avg. time spent"
              value="5m 32s"
            />
            <MetricCard
              icon={<Layers size={24} />}
              label="Avg scenes/session"
              value="1.1"
            />
            <MetricCard
              icon={<TrendingDown size={24} />}
              label="Bounce rate"
              value="62.3%"
            />
            <MetricCard
              icon={<Folder size={24} />}
              label="Active projects"
              value="2"
            />
          </div>
        </div>

        {/* Main Analytics Section */}
        <h2 className="text-2xl font-bold text-white mb-6">Engagement & Usage Overview</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
          {/* Engagement Trend - REDESIGNED */}
          <div className="bg-[#1E1F22] border border-gray-800 rounded-xl p-8 backdrop-blur-sm">
              
           <div className="mb-8">
  <h2 className="text-[20px] font-bold text-white mb-4">Engagement Trend</h2>
  <div className="flex items-baseline gap-3">
    <span className="text-[25px] font-bold text-white">68.4%</span>
    <span className="text-gray-400 text-base mb-2">Engagement rate</span>
  </div>
</div>

            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={engagementData}>
                <defs>
                  <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.secondary} stopOpacity={0.8} />
                    <stop offset="100%" stopColor={COLORS.secondary} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="0" stroke="#3A3B3E" vertical={false} />
                <XAxis 
                  dataKey="day" 
                  stroke="#666" 
                  style={{ fontSize: '14px', fontWeight: '500' }}
                  axisLine={false}
                  tickLine={false}
                  dy={10}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: `1px solid ${COLORS.secondary}`,
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Area
                  type="monotone"
                  dataKey="engagement"
                  stroke={COLORS.secondary}
                  strokeWidth={3}
                  fill="url(#engagementGradient)"
                  fillOpacity={1}
                  dot={false}
                  activeDot={{ r: 6, fill: COLORS.secondary }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>

           <div className="mt-8 pt-6 border-t border-gray-700 space-y-4">
  {/* New Users */}
  <div className="flex items-center justify-between">
    <span className="text-gray-300 font-medium w-32 text-[12px]">New Users</span>
    <div className="flex-1 mx-4 h-2.5 bg-gray-700 rounded-full overflow-hidden max-w-[280px]">
      <div className="h-full bg-[#47D176]" style={{ width: '54%' }}></div>
    </div>
    <span className="text-white font-bold w-12 text-right text-[14px]">54%</span>
  </div>

  {/* Returning Users */}
  <div className="flex items-center justify-between">
    <span className="text-gray-300 font-medium w-32 text-[12px]">Returning Users</span>
    <div className="flex-1 mx-4 h-2.5 bg-gray-700 rounded-full overflow-hidden max-w-[280px]">
      <div className="h-full bg-[#5959DC]" style={{ width: '46%' }}></div>
    </div>
    <span className="text-white font-bold w-12 text-right text-[14px]">46%</span>
  </div>
</div>

          </div>

          {/* Interaction Intensity - REDESIGNED */}
          <div className="bg-[#1E1F22] border border-gray-800 rounded-xl p-8 backdrop-blur-sm">
           <div className="mb-8 flex justify-between items-start">
  <div>
    <h2 className="text-[20px] font-bold text-white mb-4">Interaction Intensity</h2>
    <div className="flex items-baseline gap-3">
      <span className="text-[25px] font-bold text-white">34.2%</span>
      <span className="text-gray-400 text-base mb-2">Interaction rate</span>
    </div>
  </div>
  <select className="bg-[#3A3C42] text-gray-300 px-4 py-2.5 rounded-lg text-sm border border-gray-700 hover:border-gray-600 transition">
    <option>Top Performing</option>
  </select>
</div>


          <div className="space-y-3">
  {topScenes.map((scene) => (
    <div
      key={scene.id}
      className="bg-[#1C1D1D] border border-gray-700 rounded-xl p-4 hover:border-gray-600 transition"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#3A3C42' }}
        >
          <img
            src={
              scene.id === 1
                ? OverallAnalytics.intensity2
                : OverallAnalytics.intensity1
            }
            alt="Scene Icon"
            className="w-6 h-6"
          />
        </div>

        <div className="flex-1">
          <div className="text-[12px] text-gray-500 font-medium mb-1">
            {scene.id === 1 ? 'Top Scene' : 'Most Clicked Element'}
          </div>

          <h3 className="text-white font-bold text-[16px] mb-2">
            {scene.title}
          </h3>

          <div
            className="inline-block px-3 py-1 rounded-full text-[12px] font-medium"
            style={{
              backgroundColor: scene.color + '30',
              color: scene.color,
            }}
          >
            {scene.views
              ? `${(scene.views / 1000).toFixed(1)}k views`
              : `${scene.clicks} clicks`}
          </div>
        </div>
      </div>
    </div>
  ))}
</div>

          </div>

          {/* Device Distribution - REDESIGNED */}
         <div className="bg-[#1E1F22] border border-gray-800 rounded-xl p-6">
  <h2 className="text-[20px] font-bold text-white mb-6">
    Device Distribution
  </h2>
  <div className="flex flex-col items-center">
    <div className="relative w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <Pie
            data={deviceData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={120}
            dataKey="value"
            startAngle={135}
            endAngle={-225}
            cornerRadius={6}
            label={renderCustomLabel}
          >
            {deviceData.map((entry, index) => (
              <Cell key={index} fill={entry.color} stroke="none" />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Floating Labels */}
      <div className="absolute inset-0 pointer-events-none">
        {/* MR - Top Left */}
        <div className="absolute w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg" style={{ top: '15%', left: '45%', transform: 'translate(-50%, -50%)' }}>
          <span className="text-[#D9232A] font-bold text-[16px]">MR</span>
        </div>
        {/* WEB - Top Right */}
        <div className="absolute w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg" style={{ top: '25%', right: '25%', transform: 'translate(50%, -50%)' }}>
          <span className="text-[#47D176] font-bold text-[16px]">WEB</span>
        </div>
        {/* VR - Bottom Left */}
        <div className="absolute w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg" style={{ bottom: '20%', left: '35%', transform: 'translate(-50%, 50%)' }}>
          <span className="text-[#5959DC] font-bold text-[16px]">VR</span>
        </div>
      </div>
    </div>
  </div>

  {/* Legend */}
<div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-700">
  {deviceData.map((device) => (
    <div
      key={device.name}
      className="flex items-center justify-between w-full px-4 py-2 rounded-lg"
      style={{ backgroundColor: '#2A2B2E' }}
    >
      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: device.color }}
        />
        <span className="text-gray-300 text-[12px] font-medium">
          {device.name}
        </span>
      </div>

      <span className="text-white font-bold text-[14px]">
        {device.value}%
      </span>
    </div>
  ))}
</div>

</div>
        </div>

        {/* Geographic Distribution Section */}
        <div className="bg-[#1E1F22] border border-gray-800 rounded-xl p-8 mt-6">
          <h2 className="text-[20px] font-bold text-white mb-6">Geographic Distribution</h2>
          
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[16px] font-semibold text-white">Top User Locations</h3>
              <div className="flex gap-2 bg-[#3A3C42] rounded-[10px] p-1">
                <button className="bg-[#1E1F22] text-white px-6 py-2.5 rounded-[10px] text-[14px] font-semibold hover:bg-[#25272A] transition">
                  Countries
                </button>
                <button className="bg-transparent text-gray-500 px-6 py-2.5 rounded-full text-[14px] font-semibold hover:text-gray-300 transition">
                  Cities
                </button>
              </div>
            </div>

            <div className="space-y-5">
              {/* United States */}
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-[14px] w-32">United States</span>
                <div className="flex-1">
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[#5959DC] rounded-full" style={{ width: '42%' }}></div>
                  </div>
                </div>
                <span className="text-white font-bold text-[14px] w-12 text-right">42%</span>
                <span className="text-gray-400 text-[14px] w-16 text-right">12000</span>
              </div>

              {/* United Kingdom */}
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-[14px] w-32">United Kingdom</span>
                <div className="flex-1">
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[#5959DC] rounded-full" style={{ width: '24%' }}></div>
                  </div>
                </div>
                <span className="text-white font-bold text-[14px] w-12 text-right">24%</span>
                <span className="text-gray-400 text-[14px] w-16 text-right">10000</span>
              </div>

              {/* Germany */}
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-[14px] w-32">Germany</span>
                <div className="flex-1">
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[#5959DC] rounded-full" style={{ width: '18%' }}></div>
                  </div>
                </div>
                <span className="text-white font-bold text-[14px] w-12 text-right">18%</span>
                <span className="text-gray-400 text-[14px] w-16 text-right">8000</span>
              </div>

              {/* Australia */}
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-[14px] w-32">Australia</span>
                <div className="flex-1">
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[#5959DC] rounded-full" style={{ width: '14%' }}></div>
                  </div>
                </div>
                <span className="text-white font-bold text-[14px] w-12 text-right">14%</span>
                <span className="text-gray-400 text-[14px] w-16 text-right">5656</span>
              </div>

              {/* Canada */}
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-[14px] w-32">Canada</span>
                <div className="flex-1">
                  <div className="relative h-3 bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-[#5959DC] rounded-full" style={{ width: '11%' }}></div>
                  </div>
                </div>
                <span className="text-white font-bold text-[14px] w-12 text-right">11%</span>
                <span className="text-gray-400 text-[14px] w-16 text-right">2000</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardContainer>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value }) => {
  return (
    <div className="bg-[#1C1D1D] border-2 border-[#3A3A3A] rounded-[10px] p-2 hover:border-[#4A4A4A] transition-all">
      <div className="flex items-center gap-3">
        {/* Icon Circle */}
        <div className="w-12 h-12 rounded-full border-2 border-[#5B5B5B] bg-[#5B5B5B]/20 flex items-center justify-center flex-shrink-0">
          <div className="text-gray-400">
            {icon}
          </div>
        </div>
        
        {/* Text Content */}
        <div className="flex flex-col">
          <p className="text-gray-400 text-xs mb-0.5">{label}</p>
          <p className="text-white text-[14px] font-bold">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default Analytics;



// import React, { useState } from 'react';
// import { DashboardContainer } from '../../components/ui/UtilsLayout';
// import ButtonComponent from '../../components/ui/ButtonComponent';
// import { analytics, iconsPath } from '../../utils/imagePath';
// import AnalyticsDashboard from '../../components/Analytics/AnalyticsDashboard';

// const Analytics = () => {
//   // Add state to track which project is being viewed in detail
//   const [selectedProject, setSelectedProject] = useState(null);

//   const progressbar = [
//     { bg: '#F94D4C', width: '15%', name: 'Augmented Reality' },
//     { bg: '#9ABAFE', width: '10%', name: 'Virtual Reality' },
//     { bg: '#8FFF81', width: '5%', name: 'Web 3D' },
//     { bg: '#FFEC81', width: '0px', name: 'AR Glasses' },
//   ];

//   const CardData = [
//     {
//       brColor: '#F94C4C',
//       title: 'Surface Target AR',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#F94C4C',
//       title: 'Image Target AR',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#F94C4C',
//       title: 'Location Based AR',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#F94C4C',
//       title: 'Area Tracking AR',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#8FFF81',
//       title: 'Web 3D',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#9ABAFE',
//       title: 'Web-Based VR',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#9ABAFE',
//       title: 'App-Based VR',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'Web-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'App-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'Web-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'App-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'Web-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'App-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'Web-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'App-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'Web-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//     {
//       brColor: '#FFEC81',
//       title: 'App-Based Glasses',
//       exp: 3,
//       views: 100,
//     },
//   ];

//   // Data Trending Projects
//   const trendingProjects = [
//     {
//       id: 1,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       modified: '08/07/2025 03:05:49',
//       views: 10,
//       icon: analytics.analytics1,
//     },
//     {
//       id: 2,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       modified: '08/07/2025 03:05:49',
//       views: 9,
//       icon: analytics.analytics5,
//     },
//   ];

//   // Data  Recent Interactions
//   const recentInteractions = [
//     {
//       id: 1,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       interacted: '08/07/2025 03:05:49',
//       views: 8,
//       icon: analytics.analytics3,
//     },
//     {
//       id: 2,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       interacted: '08/07/2025 03:05:49',
//       views: 9,
//       icon: analytics.analytics4,
//     },
//     {
//       id: 3,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       interacted: '08/07/2025 03:05:49',
//       views: 10,
//       icon: analytics.analytics5,
//     },
//     {
//       id: 4,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       interacted: '08/07/2025 03:05:49',
//       views: 8,
//       icon: analytics.analytics6,
//     },
//     {
//       id: 5,
//       title: 'Training Module',
//       ip: '103.41.98.0607',
//       interacted: '08/07/2025 03:05:49',
//       views: 1,
//       icon: analytics.analytics7,
//     },
//   ];

//   // Handler for View Full Report button
//   const handleViewFullReport = (projectTitle) => {
//     setSelectedProject(projectTitle);
//   };

//   // Handler to go back to main view
//   const handleBackToMain = () => {
//     setSelectedProject(null);
//   };

//   // Render the detailed metrics view if a project is selected
//   if (selectedProject) {
//     return (
//       <DashboardContainer>
//         <AnalyticsDashboard
//           projectTitle={selectedProject}
//           onBack={handleBackToMain}
//         />
//       </DashboardContainer>
//     );
//   }

//   // Otherwise render the normal Analytics dashboard
//   return (
//     <DashboardContainer>
//       <div className="flex flex-col  gap-5">
//         <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
//           <div className="text-[20px] md:text-[24px] font-extrabold">
//             Your Reports & Insights
//           </div>
//           <div className="flex gap-5 justify-end items-center">
//             <div className="flex items-center gap-3">
//               <div className="">
//                 <img
//                   src={iconsPath.folderIcon}
//                   alt="folder icon"
//                   className="h-[20px] w-[20px]"
//                 />
//               </div>
//               <div className="">Projects: 11</div>
//             </div>
//             <div className="flex items-center gap-3">
//               <div className="">
//                 <img
//                   src={iconsPath.eyeIcon}
//                   alt="Eye icon"
//                   className="h-[20px] w-[20px]"
//                 />
//               </div>

//               <div className="">Views: 550</div>
//             </div>
//           </div>
//         </div>
//         <div className="flex flex-col xl:flex-row gap-7 w-full">
//           <div
//             className="flex flex-col gap-4 "
//             style={{
//               flex: 2,
//             }}
//           >
//             <div
//               className="bg-[#1E1F22] rounded-[10px] p-[25px] flex flex-col gap-2"
//               style={{
//                 boxShadow: '0px 0px 6px 0px #00000073',
//               }}
//             >
//               <div className="flex justify-between">
//                 <div className="font-bold text-[18px]">Your storage.</div>
//                 <div className="">60 GB used of 200GB</div>
//               </div>
//               <div className="w-full md:w-[700px] rounded-full min-h-[12px] bg-[#4C4C50] flex overflow-hidden">
//                 {progressbar.map((v, i) => (
//                   <div
//                     key={i}
//                     style={{
//                       backgroundColor: v.bg,
//                       height: '100%',
//                       width: v.width,
//                     }}
//                   ></div>
//                 ))}
//               </div>
//               <div className="flex gap-3 justify-evenly md:justify-start items-center flex-wrap">
//                 {progressbar.map((v, i) => (
//                   <div
//                     className="flex gap-4 items-center flex-1 min-w-[150px]"
//                     key={i}
//                   >
//                     <div
//                       className="h-[14px] w-[14px] rounded-full"
//                       style={{
//                         backgroundColor: v.bg,
//                       }}
//                     />
//                     <div className="text-[14px]">{v?.name}</div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//             <div className="md:pr-10 grid gap-1 md:gap-4 grid-cols-2 md:grid-cols-3 overflow-y-scroll max-h-[520px] over-flow custom-scrollbar">
//               {CardData.map((v, i) => (
//                 <div
//                   key={i}
//                   className=" bg-[#1E1F22] mb-2 md:mb-0 flex flex-col items-center py-4 rounded-[10px] gap-2"
//                   style={{
//                     boxShadow: '0px 0px 6px 0px #00000073',
//                     borderLeft: `11px solid ${v?.brColor}`,
//                   }}
//                 >
//                   <div className="text-[16px] md:text-[18px] text-[#C3C3C3] font-semibold">
//                     {v?.title}
//                   </div>
//                   <div className="flex justify-evenly gap-[2px] md:gap-2 w-full">
//                     <div
//                       style={{
//                         flex: 2,
//                       }}
//                       className="flex flex-col gap-2 items-center"
//                     >
//                       <div>{v?.exp}</div>
//                       <div className="text-[13px] text-[#C3C3C3]">
//                         Experiences
//                       </div>
//                     </div>
//                     <div
//                       style={{
//                         flex: 1,
//                       }}
//                       className="h-full max-w-[1px] bg-[#C3C3C3]"
//                     ></div>
//                     <div
//                       style={{
//                         flex: 2,
//                       }}
//                       className="flex flex-col gap-2 items-center"
//                     >
//                       <div>{v?.views}</div>
//                       <div className="text-[13px] text-[#C3C3C3]">Views</div>
//                     </div>
//                   </div>
//                   <ButtonComponent
//                     className={'text-[12px] bg-[#31333A] py-2 !h-[35px]'}
//                     onClick={() => handleViewFullReport(v.title)}
//                   >
//                     View Full Report
//                   </ButtonComponent>
//                 </div>
//               ))}
//             </div>
//           </div>
//           <div
//             style={{ flex: 1 }}
//             className="flex-1 rounded-[10px] bg-[#1E1F22] p-[20px] flex flex-col gap-5"
//           >
//             <div className="flex flex-col gap-3 w-full">
//               <div className="font-bold text-[16px] text-[#d9d9d9]">
//                 Trending Projects
//               </div>
//               <div className="flex flex-col bg-[#2D2F33] rounded-[10px] p-2">
//                 {trendingProjects.map((project, index) => (
//                   <div
//                     key={index}
//                     className="flex flex-col sm:flex-row items-start sm:items-center p-2 gap-3"
//                   >
//                     <div className="w-16 h-16 min-w-16 bg-[#1E1F22] rounded-md flex items-center justify-center overflow-hidden">
//                       {project.icon && (
//                         <img
//                           src={project.icon}
//                           alt={`Interaction ${index + 1}`}
//                           className="w-full h-full object-cover"
//                         />
//                       )}
//                     </div>
//                     <div className="flex-1 mt-2 sm:mt-0">
//                       <div className="flex flex-col">
//                         <div className="flex items-center gap-1 flex-wrap">
//                           <span className="text-[14px] font-bold">
//                             Project:
//                           </span>
//                           <span className="text-[14px] break-words">
//                             {project.title}
//                           </span>
//                         </div>
//                         <div className="flex items-center gap-1 flex-wrap">
//                           <span className="text-[14px] font-bold">IP:</span>
//                           <span className="text-[14px] break-words">
//                             {project.ip}
//                           </span>
//                         </div>
//                         <div className="flex items-center gap-1 flex-wrap">
//                           <span className="text-[14px] font-bold">
//                             Modified:
//                           </span>
//                           <span className="text-[14px] break-words">
//                             {project.modified}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="flex items-center gap-1 text-sm mt-2 sm:mt-0">
//                       <img
//                         src={iconsPath.eyeIcon}
//                         alt="Eye icon"
//                         className="h-[16px] w-[16px]"
//                       />
//                       <span>Views: {project.views}</span>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Recent Interactions Section */}
//             <div className="flex flex-col gap-3 flex-1 w-full">
//               <div className="font-bold text-[16px] text-[#d9d9d9]">
//                 Recent Interactions
//               </div>
//               <div className="bg-[#2D2F33] rounded-[10px] p-2 flex-1 overflow-y-auto">
//                 {recentInteractions.map((interaction, index) => (
//                   <div
//                     key={index}
//                     className="flex flex-col sm:flex-row items-start sm:items-center p-2 gap-3 mb-2"
//                   >
//                     <div className="w-16 h-16 min-w-16 bg-[#1E1F22] rounded-md flex items-center justify-center overflow-hidden">
//                       {interaction.icon && (
//                         <img
//                           src={interaction.icon}
//                           alt={`Interaction ${index + 1}`}
//                           className="w-full h-full object-cover"
//                         />
//                       )}
//                     </div>
//                     <div className="flex-1 mt-2 sm:mt-0">
//                       <div className="flex flex-col">
//                         <div className="flex items-center gap-1 flex-wrap">
//                           <span className="text-[14px] font-bold">
//                             Project:
//                           </span>
//                           <span className="text-[14px] break-words">
//                             {interaction.title}
//                           </span>
//                         </div>
//                         <div className="flex items-center gap-1 flex-wrap">
//                           <span className="text-[14px] font-bold">IP:</span>
//                           <span className="text-[14px] break-words">
//                             {interaction.ip}
//                           </span>
//                         </div>
//                         <div className="flex items-center gap-1 flex-wrap">
//                           <span className="text-[14px] font-bold">
//                             Interacted on:
//                           </span>
//                           <span className="text-[14px] break-words">
//                             {interaction.interacted}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                     <div className="flex items-center gap-2 text-sm mt-2 sm:mt-0">
//                       <img
//                         src={iconsPath.eyeIcon}
//                         alt="Eye icon"
//                         className="h-[16px] w-[16px]"
//                       />
//                       <span>Views: {interaction.views}</span>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </DashboardContainer>
//   );
// };

// export default Analytics;